import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { UploadSimple, FileText, X } from "@phosphor-icons/react";

export const WELFARE_FILES = {
  funeral_poster: { label: "Funeral Poster", mb: 1 },
  funeral_invitation: { label: "Funeral Invitation", mb: 1 },
  marriage_invitation: { label: "Marriage Invitation", mb: 1 },
  supporting_document: { label: "Supporting Document", mb: 5, pdfOnly: true },
};

export function WelfareUpload({ purpose, value, onChange, onBusy }) {
  const spec = WELFARE_FILES[purpose];
  const [phase, setPhase] = useState("");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const current = useRef({ id: null, controller: null, mounted: true });
  useEffect(() => {
    current.current.mounted = true;
    const active = current.current;
    return () => {
      active.mounted = false; active.controller?.abort();
      if (active.id) api.delete(`/welfare/uploads/${active.id}`).catch(() => {});
    };
  }, []);
  const remove = async () => {
    if (current.current.id) await api.delete(`/welfare/uploads/${current.current.id}`).catch(() => {});
    current.current.id = null; onChange(null); setProgress(0); setPhase(""); setError("");
    if (input.current) input.current.value = "";
  };
  const upload = async (file) => {
    if (!file) return;
    setError("");
    const ext = file.name.split(".").pop().toLowerCase();
    if (!(spec.pdfOnly ? ["pdf"] : ["pdf", "jpg", "jpeg"]).includes(ext)) { setError(spec.pdfOnly ? "Choose a PDF file only." : "Choose a PDF or JPEG file only."); return; }
    if (!file.size || file.size > spec.mb * 1024 * 1024) { setError(`Choose a non-empty file no larger than ${spec.mb}MB.`); return; }
    await remove(); setBusy(true); onBusy(true); setPhase("Preparing secure upload…");
    const controller = new AbortController(); current.current.controller = controller;
    try {
      const { data: init } = await api.post("/welfare/uploads", { purpose, filename: file.name, size: file.size, content_type: file.type }, { signal: controller.signal });
      current.current.id = init.id;
      for (let index = 0; index < init.chunks; index++) {
        setPhase(`Uploading ${index + 1} of ${init.chunks} parts…`);
        const chunk = file.slice(index * init.chunk_size, (index + 1) * init.chunk_size);
        await api.put(`/welfare/uploads/${init.id}/chunks/${index}`, chunk, { signal: controller.signal, headers: { "Content-Type": "application/octet-stream" },
          onUploadProgress: (event) => { if (current.current.mounted) setProgress(Math.round(((index * init.chunk_size + event.loaded) / file.size) * 85)); } });
      }
      setProgress(90); setPhase("Validating and securely saving…");
      const { data } = await api.post(`/welfare/uploads/${init.id}/complete`, {}, { signal: controller.signal });
      if (current.current.mounted) { onChange(data); setProgress(100); setPhase("Uploaded securely"); }
    } catch (err) {
      if (!controller.signal.aborted && current.current.mounted) { setError(formatApiError(err.response?.data?.detail)); setPhase("Upload not completed. Choose the file again to retry."); }
    } finally {
      if (current.current.mounted) { setBusy(false); onBusy(false); }
    }
  };
  return <div className="rounded-md border border-dashed border-border p-4 bg-background/50" data-testid={`welfare-upload-${purpose}`}>
    <Label htmlFor={`welfare-file-${purpose}`} className="font-medium">{spec.label} <span className="text-destructive">*</span></Label>
    <p className="text-xs text-muted-foreground mt-1">{spec.pdfOnly ? "PDF only" : "PDF or JPEG"} · Maximum {spec.mb}MB · Private to you and authorized reviewers</p>
    <input ref={input} id={`welfare-file-${purpose}`} data-testid={`welfare-file-${purpose}`} type="file" accept={spec.pdfOnly ? ".pdf,application/pdf" : ".pdf,.jpg,.jpeg,application/pdf,image/jpeg"} disabled={busy} onChange={(e) => upload(e.target.files?.[0])} className="sr-only" />
    <div className="flex items-center gap-3 mt-3 min-w-0">
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => input.current?.click()} data-testid={`welfare-pick-${purpose}`} className="gap-2 shrink-0"><UploadSimple />{value ? "Replace" : "Choose file"}</Button>
      {value && <><FileText className="shrink-0 text-primary" /><span className="text-xs break-all flex-1" data-testid={`welfare-filename-${purpose}`}>{value.filename}</span><Button type="button" size="icon" variant="ghost" onClick={remove} disabled={busy} data-testid={`welfare-remove-${purpose}`} aria-label={`Remove ${spec.label}`}><X /></Button></>}
    </div>
    {phase && <div className="mt-3"><div role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label={`${spec.label} upload progress`} data-testid={`welfare-progress-${purpose}`} className="h-1.5 bg-muted rounded-full overflow-hidden"><div className="bg-primary h-full transition-[width] duration-200" style={{ width: `${progress}%` }} /></div><p className="text-xs text-muted-foreground mt-2" role="status" data-testid={`welfare-upload-status-${purpose}`}>{phase}</p></div>}
    {error && <p role="alert" className="text-xs text-destructive mt-2" data-testid={`welfare-upload-error-${purpose}`}>{error}</p>}
  </div>;
}
